// Membership fee accrual + pause/resume helpers.
// Pure functions only (no Firebase) so they can be used from UI and database layers.

export const PAUSE_STATUS = {
  ACTIVE: 'active',
  COMPLETED: 'completed'
};

// Convert 'YYYY-MM-DD', ISO strings, timestamps or Date objects into a local Date
export const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const fromNumber = new Date(value);
    return isNaN(fromNumber.getTime()) ? null : fromNumber;
  }

  const str = String(value).trim();
  // Date-only values, and the UTC-midnight ISO strings the app stores for them,
  // are read as local dates so they never shift a day across timezones
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z)?$/.exec(str);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }

  const parsed = new Date(str);
  return isNaN(parsed.getTime()) ? null : parsed;
};

export const toIsoString = (value) => {
  const date = toDate(value);
  return date ? date.toISOString() : null;
};

export const startOfDay = (value) => {
  const date = toDate(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

export const formatDate = (value, fallback = 'N/A') => {
  const date = toDate(value);
  if (!date) return fallback;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Add months, clamping the day so e.g. Jan 31 + 1 month = Feb 28/29
export const addMonths = (value, months) => {
  const date = toDate(value);
  if (!date) return null;

  const result = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDayOfMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(date.getDate(), lastDayOfMonth));
  result.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
  return result;
};

export const daysBetween = (from, to) => {
  const start = startOfDay(from);
  const end = startOfDay(to);
  if (!start || !end) return 0;
  return Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
};

// Whole months elapsed between two dates (partial months are not counted)
export const wholeMonthsBetween = (from, to) => {
  const start = startOfDay(from);
  const end = startOfDay(to);
  if (!start || !end) return 0;

  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (end.getDate() < start.getDate()) months -= 1;
  return months;
};

export const getDurationMonths = (membershipDuration) => {
  switch (String(membershipDuration || 'monthly').toLowerCase().trim()) {
    case '3 months':
    case '3months':
    case 'quarterly':
      return 3;
    case '6 months':
    case '6months':
    case 'semi-annually':
      return 6;
    case '1 year':
    case 'annual':
    case 'annually':
    case 'yearly':
      return 12;
    case '1 month':
    case 'monthly':
    default:
      return 1;
  }
};

export const parseFeeAmount = (feeValue) => {
  if (typeof feeValue === 'number') return isNaN(feeValue) ? 0 : feeValue;
  if (typeof feeValue === 'string') {
    const parsed = parseFloat(feeValue.replace(/[^\d.-]/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

// The date fee calculation currently runs from. Resuming a membership moves this
// forward to the resume date; `registrationDate` always keeps the original value.
export const getEffectiveRegistrationDate = (member) =>
  member?.effectiveRegistrationDate ||
  member?.registrationDate ||
  member?.originalRegistrationDate ||
  member?.createdAt ||
  null;

export const getOriginalRegistrationDate = (member) =>
  member?.originalRegistrationDate ||
  member?.registrationDate ||
  member?.createdAt ||
  null;

// Realtime Database can return arrays as keyed objects, so normalize both shapes
export const getPauseHistory = (member) => {
  const raw = member?.pauseHistory;
  if (!raw) return [];

  const entries = Array.isArray(raw) ? raw : Object.values(raw);

  return entries
    .filter(Boolean)
    .map((entry, index) => {
      const pauseDate = entry.pauseDate || entry.startDate || null;
      const resumeDate = entry.resumeDate || entry.endDate || null;
      const status = entry.status || (resumeDate ? PAUSE_STATUS.COMPLETED : PAUSE_STATUS.ACTIVE);

      return {
        ...entry,
        id: entry.id || `pause_${index}`,
        pauseDate,
        resumeDate,
        status,
        reason: entry.reason || entry.pauseReason || 'Not specified',
        durationDays: resumeDate
          ? daysBetween(pauseDate, resumeDate)
          : daysBetween(pauseDate, new Date())
      };
    })
    .sort((a, b) => (toDate(a.pauseDate)?.getTime() || 0) - (toDate(b.pauseDate)?.getTime() || 0));
};

export const getActivePause = (member) => {
  const history = getPauseHistory(member);
  const active = [...history].reverse().find((entry) => entry.status === PAUSE_STATUS.ACTIVE);
  if (active) return active;

  // Fall back to the legacy single-pause fields for members paused before pause history existed
  if (member?.membershipStatus === 'paused' && member?.pauseStartDate) {
    return {
      id: 'legacy_pause',
      pauseDate: member.pauseStartDate,
      resumeDate: null,
      reason: member.pauseReason || 'Not specified',
      status: PAUSE_STATUS.ACTIVE,
      durationDays: daysBetween(member.pauseStartDate, new Date()),
      legacy: true
    };
  }

  return null;
};

export const isMembershipPaused = (member) =>
  member?.membershipStatus === 'paused' || Boolean(getActivePause(member));

export const summarizePauseHistory = (member) => {
  const storedHistory = getPauseHistory(member);
  const activePause = getActivePause(member);

  // Members paused before pause history existed only have the legacy fields,
  // so surface that pause as a history entry too
  const history =
    activePause?.legacy && !storedHistory.some((entry) => entry.id === activePause.id)
      ? [...storedHistory, activePause]
      : storedHistory;

  const completed = history.filter((entry) => entry.status === PAUSE_STATUS.COMPLETED);

  return {
    history,
    activePause,
    isPaused: isMembershipPaused(member),
    hasBeenPaused: history.length > 0 || Boolean(activePause),
    totalPauses: history.length || (activePause ? 1 : 0),
    completedPauses: completed.length,
    totalPausedDays: history.reduce((total, entry) => total + (entry.durationDays || 0), 0),
    lastPause: history.length > 0 ? history[history.length - 1] : activePause
  };
};

/**
 * Calculate how much a member has been charged to date.
 *
 * Fees accrue one billing period at a time starting from the effective
 * registration date. While a membership is paused, accrual stops on the pause
 * date; on resume, the resume date becomes the new effective registration date
 * and everything charged before the pause is carried forward.
 */
export const calculateMembershipFees = (member, options = {}) => {
  const { asOfDate = new Date(), totalPaid } = options;

  const feeAmount = parseFeeAmount(member?.fees);
  const durationMonths = getDurationMonths(member?.membershipDuration);
  const activePause = getActivePause(member);
  const isPaused = isMembershipPaused(member);

  const anchorDate = toDate(getEffectiveRegistrationDate(member));
  const asOf = toDate(asOfDate) || new Date();
  const pauseDate = activePause ? toDate(activePause.pauseDate) : null;

  // Fee calculation stops on the pause date while paused
  const accrualEndDate = isPaused && pauseDate ? pauseDate : asOf;

  const carriedPeriods = Number(member?.carriedChargedPeriods) || 0;
  const carriedAmount = Number(member?.carriedChargedAmount) || 0;

  let currentCyclePeriods = 0;
  if (anchorDate && startOfDay(accrualEndDate) >= startOfDay(anchorDate)) {
    const monthsElapsed = wholeMonthsBetween(anchorDate, accrualEndDate);
    currentCyclePeriods = Math.floor(monthsElapsed / durationMonths) + 1;
  }

  const currentCycleCharged = round2(currentCyclePeriods * feeAmount);
  const periodsCharged = carriedPeriods + currentCyclePeriods;
  const totalCharged = round2(carriedAmount + currentCycleCharged);

  const paid = Number.isFinite(totalPaid) ? totalPaid : (Number(member?.totalPaid) || 0);
  const balanceDue = round2(Math.max(0, totalCharged - paid));
  const creditBalance = round2(Math.max(0, paid - totalCharged));

  const nextChargeDate =
    !isPaused && anchorDate ? addMonths(anchorDate, currentCyclePeriods * durationMonths) : null;

  return {
    feeAmount,
    durationMonths,
    isPaused,
    pauseDate: pauseDate ? pauseDate.toISOString() : null,
    effectiveRegistrationDate: anchorDate ? anchorDate.toISOString() : null,
    originalRegistrationDate: toIsoString(getOriginalRegistrationDate(member)),
    accrualEndDate: toDate(accrualEndDate)?.toISOString() || null,
    carriedChargedPeriods: carriedPeriods,
    carriedChargedAmount: round2(carriedAmount),
    currentCyclePeriods,
    currentCycleCharged,
    periodsCharged,
    totalCharged,
    totalPaid: round2(paid),
    balanceDue,
    creditBalance,
    isFullyPaid: balanceDue === 0,
    nextChargeDate: nextChargeDate ? nextChargeDate.toISOString() : null
  };
};
